import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { api, formatErr } from "../../lib/api";
import { FormRow, grid2, addMonths } from "./adminUi";

const EMPTY = { player_name: "", email: "", phone: "", lead_id: null, user_id: null, plan_key: "", duration_months: 3,
  payment_date: "", payment_status: "pending", activation_date: "", end_date: "", welcome_message: "", notes: "" };

export const CreateOnboardingDialog = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const [f, setF] = useState(EMPTY);
  const [src, setSrc] = useState("lead");
  const [leads, setLeads] = useState([]);
  const [clients, setClients] = useState([]);
  const [cat, setCat] = useState({ plans: [], durations_months: [] });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setF(EMPTY);
    Promise.all([api.get("/admin/captacion"), api.get("/admin/clients"), api.get("/admin/client-management/plans"), api.get("/admin/onboarding-settings")])
      .then(([l, c, p, s]) => {
        setLeads(l.data); setClients(c.data); setCat(p.data);
        setF((x) => ({ ...x, plan_key: x.plan_key || p.data.plans[0]?.key || "", welcome_message: x.welcome_message || s.data.default_welcome_message }));
      }).catch((e) => toast.error(formatErr(e)));
  }, [open]);

  const set = (patch) => setF((x) => ({ ...x, ...patch }));
  const pickLead = (id) => {
    const l = leads.find((x) => x.id === id);
    set(l ? { lead_id: l.id, user_id: null, player_name: l.player_name, email: l.email || "", phone: l.phone || "" } : { lead_id: null });
  };
  const pickClient = (id) => {
    const c = clients.find((x) => x.id === id);
    set(c ? { user_id: c.id, lead_id: null, player_name: c.name, email: c.email || "", phone: c.phone || "" } : { user_id: null });
  };
  const end = f.end_date || addMonths(f.activation_date, f.duration_months);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { ...f, end_date: end || null };
    ["email", "phone", "payment_date", "activation_date", "notes"].forEach((k) => { if (!body[k]) body[k] = null; });
    try {
      const { data } = await api.post("/admin/onboardings", body);
      toast.success("Incorporación creada");
      onOpenChange(false);
      navigate(`/app/admin/incorporaciones/${data.id}`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 409 && d?.id) {
        toast.error(d.message);
        onOpenChange(false);
        navigate(`/app/admin/incorporaciones/${d.id}`);
      } else toast.error(formatErr(err));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" style={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" }} data-testid="onb-create-dialog">
        <DialogHeader><DialogTitle style={{ fontFamily: "Bebas Neue, sans-serif", fontSize: 26, letterSpacing: "0.05em" }}>Nueva incorporación</DialogTitle></DialogHeader>
        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {[["lead", "Desde un lead"], ["client", "Cliente existente"], ["new", "Nuevo"]].map(([k, l]) => (
              <button type="button" key={k} onClick={() => { setSrc(k); set({ lead_id: null, user_id: null }); }} data-testid={`onb-src-${k}`}
                className={src === k ? "qv-btn-primary" : "qv-btn-ghost"} style={{ padding: "8px 14px", fontSize: 12 }}>{l}</button>
            ))}
          </div>
          {src === "lead" && (
            <FormRow label="Lead"><select className="qv-input" value={f.lead_id || ""} onChange={(e) => pickLead(e.target.value)} data-testid="onb-pick-lead">
              <option value="">Elige un lead…</option>
              {leads.map((l) => <option key={l.id} value={l.id}>{l.player_name} · {l.age} años · {l.email}</option>)}
            </select></FormRow>
          )}
          {src === "client" && (
            <FormRow label="Cliente"><select className="qv-input" value={f.user_id || ""} onChange={(e) => pickClient(e.target.value)} data-testid="onb-pick-client">
              <option value="">Elige un cliente…</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name} · {c.email}</option>)}
            </select></FormRow>
          )}
          <div style={grid2}>
            <FormRow label="Nombre del futbolista *"><input className="qv-input" required minLength={2} value={f.player_name} onChange={(e) => set({ player_name: e.target.value })} data-testid="onb-name" /></FormRow>
            <FormRow label="Email"><input className="qv-input" type="email" value={f.email} onChange={(e) => set({ email: e.target.value })} data-testid="onb-email" /></FormRow>
            <FormRow label="Teléfono"><input className="qv-input" value={f.phone} placeholder="+34 600 000 000" onChange={(e) => set({ phone: e.target.value })} data-testid="onb-phone" /></FormRow>
            <FormRow label="Plan contratado *"><select className="qv-input" value={f.plan_key} onChange={(e) => set({ plan_key: e.target.value })} data-testid="onb-plan">
              {cat.plans.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
            </select></FormRow>
            <FormRow label="Duración *"><select className="qv-input" value={f.duration_months} onChange={(e) => set({ duration_months: Number(e.target.value), end_date: "" })} data-testid="onb-duration">
              {cat.durations_months.map((m) => <option key={m} value={m}>{m} {m === 1 ? "mes" : "meses"}</option>)}
            </select></FormRow>
            <FormRow label="Fecha de pago"><input className="qv-input" type="date" value={f.payment_date} onChange={(e) => set({ payment_date: e.target.value })} data-testid="onb-payment-date" /></FormRow>
            <FormRow label="Estado del pago"><select className="qv-input" value={f.payment_status} onChange={(e) => set({ payment_status: e.target.value })} data-testid="onb-payment-status">
              <option value="pending">Pendiente</option><option value="confirmed">Confirmado</option><option value="rejected">Rechazado</option>
            </select></FormRow>
            <FormRow label="Fecha de activación"><input className="qv-input" type="date" value={f.activation_date} onChange={(e) => set({ activation_date: e.target.value, end_date: "" })} data-testid="onb-activation" /></FormRow>
            <FormRow label="Finalización prevista"><input className="qv-input" type="date" value={end} onChange={(e) => set({ end_date: e.target.value })} data-testid="onb-end" /></FormRow>
          </div>
          <FormRow label="Mensaje de bienvenida ({nombre} = nombre del futbolista)">
            <textarea className="qv-input" rows={6} value={f.welcome_message} onChange={(e) => set({ welcome_message: e.target.value })} data-testid="onb-welcome" style={{ textTransform: "none", letterSpacing: 0, minHeight: 150, height: "auto" }} />
          </FormRow>
          <button className="qv-btn-primary" disabled={busy || !f.player_name || !f.plan_key} style={{ justifyContent: "center" }} data-testid="onb-create-submit">
            {busy ? "Creando…" : "Crear incorporación"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
